import React from 'react';
import { List, Avatar, Button } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { OrganizationUsersResponse, UserInvitation } from '../../../../api/organization';
import { OrganizationAccessRoles } from '../../../../common/auth';
import BaseDropdown from '../../../../components/UI/Dropdown/BaseDropdown';

interface UserManagementItemProps {
  item: OrganizationUsersResponse | UserInvitation;
  currentUserOrgAccess: any;
  getItems: (item: any) => any[];
  handleMenuClick: (e: any, item: any) => void;
  getUserRoleComponent: (item: any) => React.ReactNode;
  getProfilePictureUrl: (user: any) => string;
  getUserInitials: (name: string) => string;
  getLabelForUserStateInOrganization: (state: any) => string;
}

const UserManagementItem: React.FC<UserManagementItemProps> = ({
  item,
  currentUserOrgAccess,
  getItems,
  handleMenuClick,
  getUserRoleComponent,
  getProfilePictureUrl,
  getUserInitials,
  getLabelForUserStateInOrganization,
}) => {
  const isUser = item.type === 'user';

  const getActions = (isUser: boolean) => {
    const dropdownButton = (
      <BaseDropdown
        key={isUser ? item.User?.id : item.id}
        menu={{
          items: getItems(item),
          onClick: (e) => handleMenuClick(e, item),
        }}
        trigger={['click']}
      >
        <Button
          icon={<MoreOutlined color="#707792" />}
          type="default"
          className="border-0 hover:bg-[#0000000F] shadow-none"
        />
      </BaseDropdown>
    );

    if (isUser) {
      const isCurrentUserAdmin = currentUserOrgAccess?.roleToAssume === OrganizationAccessRoles.ADMIN;
      const isNotCurrentUser = currentUserOrgAccess?.userId !== item.User.id;

      if (isCurrentUserAdmin && isNotCurrentUser) {
        return [dropdownButton];
      } else {
        return [
          <Button
            icon={<MoreOutlined color="#707792" />}
            type="default"
            className="border-0 hover:bg-[#0000000F] shadow-none opacity-0 cursor-default"
          />,
        ];
      }
    } else {
      return [dropdownButton];
    }
  };

  const capitalised = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  return (
    <List.Item actions={getActions(isUser)} className="flex !p-2 !pl-5 !pr-5 m-0">
      <div className="flex-[3_3_0%] w-96">
        {isUser ? (
          <div className="user-info flex justify-start gap-2">
            <div className="flex justify-center items-center">
              <Avatar src={getProfilePictureUrl(item.User)}>
                {getUserInitials(item.User.fullName || item.User.email)}
              </Avatar>
            </div>
            <div className="flex flex-col">
              <span className="user-info__name">{sliceEmailIfNeeded(item.User.fullName || item.User.email)}</span>
              <span className="user-info__email">{sliceEmailIfNeeded(item.User.email)}</span>
            </div>
          </div>
        ) : (
          <div className="user-info flex justify-start gap-2">
            <div className="flex justify-center items-center">
              <Avatar>{getUserInitials(item.email)}</Avatar>
            </div>
            <div className="flex flex-col">
              <span className="user-info__name">
                <span className="font-medium">{sliceEmailIfNeeded(item.email)}</span>
              </span>
              <span className="user-info__email">{sliceEmailIfNeeded(item.email)}</span>
            </div>
          </div>
        )}
      </div>
      <div className="text-center flex-1">{getUserRoleComponent(item)}</div>
      <div className="text-center flex-1">
        {isUser ? getLabelForUserStateInOrganization(item.usersStateInOrganization) : capitalised(item.status)}
      </div>
      <div className="text-center flex-1 opacity-60">
        {isUser ? dayjs(new Date(item.createdAt)).format('MMM DD, YYYY') : ''}
      </div>
    </List.Item>
  );
};

function sliceEmailIfNeeded(email: string) {
  if (email.length > 40) {
    return email.substring(0, 40) + '...';
  }
  return email;
}

export default UserManagementItem;
